import { useState, useEffect, useCallback, useRef } from "react";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";
import SharedToast from "../components/Toast";

const API_BASE = process.env.REACT_APP_API_URL || "";

function getToken() {
  const match = document.cookie.match(/(?:^|; )token=([^;]*)/);
  if (match) return decodeURIComponent(match[1]);
  return localStorage.getItem("token") || "";
}

/* แสดงทีละ 50 รายการล่าสุด — มีปุ่มไปหน้าถัดไป หรือกดแสดงทั้งหมด */
const PAGE_SIZE = 50;

/* ตารางกว้าง 1140px ขั้นต่ำ จอแคบกว่านี้ต้องเลื่อนแนวนอนทั้งหน้าซึ่งใช้งานไม่ไหว
   ต่ำกว่า 860 เลยเปลี่ยนไป render เป็นการ์ดแทน
   ไฟล์นี้ใช้ inline style ล้วน เขียน media query ไม่ได้ ต้องเช็คด้วย matchMedia */
function useIsNarrow(breakpoint = 860) {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia(`(max-width:${breakpoint}px)`).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${breakpoint}px)`);
    const sync = () => setNarrow(mq.matches);
    mq.addEventListener("change", sync);
    /* ฟัง resize ควบด้วย — บาง environment (devtools/emulator ปรับขนาดจอ) เปลี่ยน viewport
       โดยไม่ยิง event change ของ matchMedia ทำให้ค้างอยู่ layout เดิม */
    window.addEventListener("resize", sync);
    sync();
    return () => {
      mq.removeEventListener("change", sync);
      window.removeEventListener("resize", sync);
    };
  }, [breakpoint]);
  return narrow;
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("th-TH", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}
function fmtMoney(n) {
  return Number(n || 0).toLocaleString() + " ฿";
}

// walk-in เก็บชื่อไว้ที่ guestName ส่วนจองปกติเก็บแยก firstName/lastName
function bookerName(bk, payment) {
  const name = [bk.firstName, bk.lastName].filter(Boolean).join(" ");
  return name || bk.guestName || payment.email || "—";
}

/* Payment ผูกกับ booking ได้ใบเดียว (ใบแรกของกลุ่ม) — จองหลายห้องต้องอ่านจาก payment.group
   ที่ backend สรุปมาให้ ไม่งั้นจะเห็นห้องเดียวและยอดผิด
   ข้อมูลเก่าที่ไม่มี groupId จะไม่มี group → ถอยไปใช้ booking ใบเดียวแบบเดิม */
function summarize(payment) {
  const bk = payment.bookingId || {};
  const g  = payment.group;

  if (g) {
    return { count: g.size, total: g.total, paid: g.paid,
             remaining: g.remaining, types: g.types || [], rooms: g.rooms || [] };
  }

  const total = Number(bk.totalAmount) || 0;
  const paid  = Number(bk.paidAmount)  || 0;
  return {
    count: bk._id ? 1 : 0,
    total, paid, remaining: Math.max(0, total - paid),
    types: bk.roomType?.name ? [{ name: bk.roomType.name, qty: 1 }] : [],
    rooms: bk.roomUnit?.roomNumber
      ? [{ typeName: bk.roomType?.name || "—", roomNumber: bk.roomUnit.roomNumber, total, paid, status: bk.status }]
      : [],
  };
}

// booking ที่สร้างก่อนมีฟิลด์ payType ต้องเดาเอาจากยอดที่จ่ายเทียบยอดรวม
function resolvePayType(bk) {
  if (bk.payType) return bk.payType;
  const total = Number(bk.totalAmount) || 0;
  const paid  = Number(bk.paidAmount)  || 0;
  if (total > 0 && paid >= total) return "full";
  if (paid > 0) return "partial";
  return "cash";
}

const STATUS_LABEL_KEY = { Pending: "slipTabPending", Approved: "slipTabApproved", Rejected: "slipTabRejected" };
const STATUS_COLORS = {
  Pending:  { bg: "#fef3c7", color: "#92400e", border: "#fde68a", dot: "#f59e0b" },
  Approved: { bg: "#d1fae5", color: "#065f46", border: "#6ee7b7", dot: "#10b981" },
  Rejected: { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5", dot: "#ef4444" },
};

const PAYTYPE_LABEL_KEY = { full: "payTypeFull", partial: "payTypePartial", cash: "payTypeCash" };
const PAYTYPE_COLORS = {
  full:    { bg: "#d1fae5", color: "#065f46", border: "#6ee7b7" },
  partial: { bg: "#fef3c7", color: "#92400e", border: "#fde68a" },
  cash:    { bg: "#e0e7ff", color: "#3730a3", border: "#a5b4fc" },
};

/* ── Tab config ─────────────────────────────────────────────── */
const TABS = [
  { key: "All",      labelKey: "slipTabAll",      dot: "#9ca3af" },
  { key: "Pending",  labelKey: "slipTabPending",  dot: "#f59e0b" },
  { key: "NoSlip",   labelKey: "slipTabNoSlip",   dot: "#3b82f6" },
  { key: "Approved", labelKey: "slipTabApproved", dot: "#10b981" },
  { key: "Rejected", labelKey: "slipTabRejected", dot: "#ef4444" },
];

function StatusPill({ status, lang: pillLang }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.Pending;
  return (
    <span style={{
      display: "inline-block", fontSize: 11, padding: "2px 10px",
      borderRadius: 20, fontWeight: 600,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      {STATUS_LABEL_KEY[status] ? t(pillLang, STATUS_LABEL_KEY[status]) : status}
    </span>
  );
}

function PayTypePill({ type, lang: pillLang }) {
  const s = PAYTYPE_COLORS[type] || PAYTYPE_COLORS.cash;
  return (
    <span style={{
      display: "inline-block", fontSize: 11, padding: "2px 10px",
      borderRadius: 20, fontWeight: 600,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>
      {t(pillLang, PAYTYPE_LABEL_KEY[type] || "payTypeCash")}
    </span>
  );
}

function Toast({ msg, type, visible }) {
  const { lang } = useLanguage();
  return <SharedToast show={visible} type={type} message={msg} lang={lang} />;
}

/* ─── Slip Upload Widget ─────────────────────────────────────── */
function SlipUploader({ paymentId, onSuccess, showToast, lang }) {
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef              = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("slip", file);
      const res = await fetch(`${API_BASE}/api/admin/payments/${paymentId}/slip`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: "include",
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      showToast(t(lang, "slipToastUploadOk"), "success");
      onSuccess(data.payment);
    } catch (err) {
      showToast(err.message || t(lang, "slipToastUploadFail"), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      border: "1.5px dashed #93c5fd", borderRadius: 10,
      background: "#f0f9ff", padding: 16, marginTop: 12,
    }}>
      <p style={{
        margin: "0 0 10px", fontSize: 12, fontWeight: 700,
        color: "#0369a1", textTransform: "uppercase", letterSpacing: 1,
      }}>
        {t(lang, "slipUpload")}
      </p>
      {preview ? (
        <div style={{ position: "relative", marginBottom: 10 }}>
          <img src={preview} alt="slip preview"
            style={{
              width: "100%", maxHeight: 200, objectFit: "contain",
              borderRadius: 8, border: "1px solid #bae6fd",
              background: "#fff", display: "block",
            }}
          />
          <button onClick={() => { setFile(null); setPreview(null); }}
            style={{
              position: "absolute", top: 6, right: 6,
              background: "#fff", border: "1px solid #e2e8f0",
              borderRadius: 6, fontSize: 11, padding: "2px 8px",
              cursor: "pointer", color: "#64748b",
            }}>
            {t(lang, "slipChange")}
          </button>
        </div>
      ) : (
        <button onClick={() => inputRef.current?.click()}
          style={{
            width: "100%", height: 72, background: "#fff",
            border: "1px solid #bae6fd", borderRadius: 8,
            fontSize: 13, color: "#0369a1", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 8, marginBottom: 10,
          }}>
          {t(lang, "slipClickSelect")}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} hidden />
      {file && (
        <button onClick={handleUpload} disabled={loading}
          style={{
            width: "100%", height: 36, fontSize: 13, fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            background: loading ? "#e0f2fe" : "#0369a1",
            color: loading ? "#0369a1" : "#fff",
            border: "none", borderRadius: 8, transition: "all .2s",
          }}>
          {loading ? t(lang, "slipUploading") : t(lang, "slipSaveSlip")}
        </button>
      )}
    </div>
  );
}

/* ─── Modal ──────────────────────────────────────────────────── */
function PaymentModal({ payment: initPayment, onClose, onUpdate, showToast, lang }) {
  const [payment, setPayment] = useState(initPayment);
  const [note, setNote]       = useState(initPayment?.adminNote || "");
  const [loading, setLoading] = useState(false);
  const [imgErr, setImgErr]   = useState(false);

  useEffect(() => {
    setPayment(initPayment);
    setNote(initPayment?.adminNote || "");
    setImgErr(false);
  }, [initPayment]);

  if (!payment) return null;

  const bk = payment.bookingId || {};
  const rt = bk.roomType || {};
  const ru = bk.roomUnit || {};
  const isBookingCancelled = bk.status === "Cancelled" || bk.status === "PendingCancel";
  /* จ่ายเงินสด = ไม่มีสลิปให้ตรวจ ไม่ต้องโชว์กล่องอัปโหลด
     ต้องอ่าน bk.payType ตรงๆ ห้ามใช้ resolvePayType เพราะมันเดา "cash" ให้ booking เก่าที่ยังไม่จ่าย */
  const isCash = bk.payType === "cash";

  async function handleAction(status) {
    setLoading(true);
    await onUpdate(payment._id, status, note);
    setLoading(false);
    onClose();
  }

  const handleSlipUploaded = (updatedPayment) => {
    setPayment(prev => ({ ...prev, ...updatedPayment }));
    setImgErr(false);
  };

  const payType   = resolvePayType(bk);
  // ไม่บังคับแนบสลิปก่อนอนุมัติ — admin/staff ตัดสินใจเองได้ (เช่น จ่ายเงินสด หรือยืนยันยอดจากช่องทางอื่น)
  const cannotApprove = loading;

  /* ยอดต้องเป็นของทั้งกลุ่ม ไม่ใช่ของ booking ใบแรกใบเดียว */
  const sum       = summarize(payment);
  const isGroup   = sum.count > 1;
  const total     = sum.total;
  const paid      = sum.paid;
  const remaining = sum.remaining;

  const rows = [
    { label: t(lang, "slipColBooker"),  value: bookerName(bk, payment) },
    { label: t(lang, "slipEmail"),      value: payment.email || "—" },
    // จองหลายห้องแจกแจงในบล็อกรายการห้องด้านล่างแทน แถวนี้บอกแค่ภาพรวม
    { label: t(lang, "slipRoom"),       value: isGroup
        ? t(lang, "slipRoomsSummary", sum.count, sum.types.length)
        : `${rt.name || "—"} — ${ru.roomNumber || "—"}` },
    { label: t(lang, "slipColPayType"), value: <PayTypePill type={payType} lang={lang} /> },
    { label: t(lang, "slipColPaid"),    value: fmtMoney(paid) },
    { label: t(lang, "slipColTotal"),   value: fmtMoney(total) },
    { label: t(lang, "slipRemaining"),  value: fmtMoney(remaining) },
    { label: t(lang, "slipStatus"),     value: <StatusPill status={payment.status} lang={lang} /> },
    { label: t(lang, "slipSentAt"),     value: fmtDate(payment.createdAt) },
  ];

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 200, padding: 16,
      }}>
      <div style={{
        background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0",
        padding: 24, maxWidth: 460, width: "100%",
        maxHeight: "92vh", overflowY: "auto",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 600, color: "#1e293b", margin: 0 }}>
              {t(lang, "slipDetailTitle")}
            </h2>
            {!payment.slip && payment.status === "Pending" && (
              <span style={{
                fontSize: 11, padding: "2px 8px", borderRadius: 20,
                background: isCash ? PAYTYPE_COLORS.cash.bg     : "#fef9c3",
                color:      isCash ? PAYTYPE_COLORS.cash.color  : "#713f12",
                border: `1px solid ${isCash ? PAYTYPE_COLORS.cash.border : "#fef08a"}`,
                fontWeight: 600,
                display: "inline-block", marginTop: 4,
              }}>
                {t(lang, isCash ? "slipCashBooking" : "slipStaffBooking")}
              </span>
            )}
          </div>
          <button onClick={onClose}
            style={{ background: "none", border: "none", fontSize: 18,
              cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ border: "1px solid #e2e8f0", borderRadius: 8,
          overflow: "hidden", marginBottom: 16 }}>
          {rows.map((r, i) => (
            <div key={i} style={{
              display: "flex", justifyContent: "space-between",
              alignItems: "center", padding: "9px 14px",
              borderBottom: i < rows.length - 1 ? "1px solid #f3f4f6" : "none",
              fontSize: 13, gap: 8,
            }}>
              <span style={{ color: "#64748b", flexShrink: 0 }}>{r.label}</span>
              <span style={{
                fontWeight: 500, color: "#1e293b", textAlign: "right",
                maxWidth: "60%", overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {r.value}
              </span>
            </div>
          ))}
        </div>

        {/* จองหลายห้อง — แจกแจงรายห้องตรงนี้ (ตารางหน้าหลักโชว์แค่สรุป) */}
        {isGroup && sum.rooms.length > 0 && (
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 8,
            overflow: "hidden", marginBottom: 16 }}>
            <div style={{ padding: "8px 14px", background: "#f8fafc",
              fontSize: 11, fontWeight: 700, color: "#64748b",
              textTransform: "uppercase", letterSpacing: .6,
              borderBottom: "1px solid #e2e8f0" }}>
              {t(lang, "slipRoomList")}
            </div>
            {sum.rooms.map((r, i) => (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "9px 14px", fontSize: 13, gap: 8,
                borderBottom: i < sum.rooms.length - 1 ? "1px solid #f3f4f6" : "none",
              }}>
                <span style={{ color: "#1e293b", overflow: "hidden",
                  textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.typeName}
                  {/* ชื่อประเภทกับป้ายเลขห้องขึ้นต้นด้วย "ห้อง" ทั้งคู่ 6px เดิม (ที่พอสำหรับ "#14")
                      ทำให้อ่านติดกัน — เว้นให้ห่างขึ้น */}
                  <span style={{ color: "#94a3b8", marginLeft: 12 }}>
                    {t(lang, "roomNoLabel")} {r.roomNumber}
                  </span>
                </span>
                <span style={{ fontWeight: 500, color: "#1e293b", flexShrink: 0 }}>
                  {fmtMoney(r.total)}
                </span>
              </div>
            ))}
          </div>
        )}

        {payment.slip && !imgErr && (
          <img src={`${API_BASE}/uploads/slips/${payment.slip}`}
            alt={t(lang, "slipImgAlt")} onError={() => setImgErr(true)}
            style={{
              width: "100%", maxHeight: 260, objectFit: "contain",
              borderRadius: 8, border: "1px solid #e2e8f0",
              background: "#f8fafc", marginBottom: 12, display: "block",
            }}
          />
        )}
        {payment.slip && imgErr && (
          <div style={{
            width: "100%", height: 80, display: "flex",
            alignItems: "center", justifyContent: "center",
            background: "#f8fafc", border: "1px dashed #cbd5e1",
            borderRadius: 8, fontSize: 13, color: "#94a3b8", marginBottom: 12,
          }}>—</div>
        )}

        {!payment.slip && payment.status === "Pending" && !isCash && (
          <SlipUploader
            paymentId={payment._id}
            onSuccess={handleSlipUploaded}
            showToast={showToast}
            lang={lang}
          />
        )}

        <textarea value={note} onChange={e => setNote(e.target.value)}
          placeholder={t(lang, "slipAdminNote")} rows={3}
          style={{
            width: "100%", fontSize: 13, padding: "8px 10px",
            borderRadius: 8, border: "1px solid #cbd5e1", color: "#1e293b",
            resize: "vertical", marginTop: 12, marginBottom: 12,
            fontFamily: "inherit", boxSizing: "border-box",
          }}
        />

        {isBookingCancelled ? (
          <div style={{ marginBottom: 12 }}>
            <div style={{
              padding: "10px 14px", borderRadius: 8, marginBottom: 12,
              background: "#fee2e2", border: "1px solid #fca5a5",
              fontSize: 13, color: "#991b1b", fontWeight: 600, textAlign: "center",
            }}>
              ⚠️ {bk.status === "PendingCancel" ? t(lang, "slipPendingCancel") : t(lang, "slipCancelled")}
            </div>
            <button onClick={onClose}
              style={{
                width: "100%", height: 38, fontSize: 13, cursor: "pointer",
                background: "#f1f5f9", color: "#475569",
                border: "1px solid #e2e8f0", borderRadius: 8,
              }}>
              {t(lang, "slipClose")}
            </button>
          </div>
        ) : payment.status === "Pending" ? (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => handleAction("Approved")} disabled={cannotApprove}
              style={{
                flex: 1, height: 38, fontSize: 13, fontWeight: 600,
                cursor: cannotApprove ? "not-allowed" : "pointer",
                background: "#d1fae5", color: "#065f46",
                border: "1px solid #6ee7b7", borderRadius: 8,
                opacity: cannotApprove ? 0.5 : 1,
              }}>
              {t(lang, "slipApprove")}
            </button>
            <button onClick={() => handleAction("Rejected")} disabled={loading}
              style={{
                flex: 1, height: 38, fontSize: 13, fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                background: "#fee2e2", color: "#991b1b",
                border: "1px solid #fca5a5", borderRadius: 8,
                opacity: loading ? 0.6 : 1,
              }}>
              {t(lang, "slipReject")}
            </button>
          </div>
        ) : (
          <button onClick={onClose}
            style={{
              width: "100%", height: 38, fontSize: 13, cursor: "pointer",
              background: "#f1f5f9", color: "#475569",
              border: "1px solid #e2e8f0", borderRadius: 8,
            }}>
            {t(lang, "slipClose")}
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function SlipVerificationPage() {
  const { lang } = useLanguage();
  const [payments,     setPayments]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [forbidden,    setForbidden]    = useState(false);
  const [activeTab,    setActiveTab]    = useState("All");   // ✅ tab state
  const [search,       setSearch]       = useState("");
  const [selected,     setSelected]     = useState(null);
  const [page,         setPage]         = useState(0);      // หน้าที่กำลังดู (เริ่มที่ 0)
  const [showAll,      setShowAll]      = useState(false);  // กด "แสดงทั้งหมด" แล้วข้ามการแบ่งหน้า
  const narrow = useIsNarrow();
  const [toast,        setToast]        = useState({ visible: false, msg: "", type: "" });

  const showToast = (msg, type = "info") => {
    setToast({ visible: true, msg, type });
    setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 2500);
  };

  const loadPayments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/payments`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: "include",
      });
      if (res.status === 403) { setForbidden(true); setLoading(false); return; }
      const data = await res.json();
      setPayments(Array.isArray(data) ? data : []);
    } catch {
      showToast(t(lang, "slipToastLoadFail"), "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  /* เปลี่ยน tab หรือพิมพ์ค้นหาแล้วจำนวนรายการเปลี่ยน ถ้าไม่รีเซ็ตจะค้างอยู่หน้าที่ไม่มีข้อมูล */
  useEffect(() => { setPage(0); }, [activeTab, search]);

  async function handleUpdate(id, status, adminNote) {
    try {
      const res = await fetch(`${API_BASE}/api/admin/payments/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        credentials: "include",
        body: JSON.stringify({ status, adminNote }),
      });
      if (!res.ok) throw new Error();
      /* ปฏิเสธสลิป = หลังบ้านยกเลิกการจองให้แล้ว สะท้อนใน state ทันทีจะได้ไม่ต้องกด refresh */
      setPayments(prev =>
        prev.map(p => {
          if (p._id !== id) return p;
          const next = { ...p, status, adminNote };
          if (status === "Rejected" && next.bookingId) {
            next.bookingId = { ...next.bookingId, status: "Cancelled" };
          }
          return next;
        })
      );
      showToast(
        status === "Approved" ? t(lang, "slipToastApproved") : t(lang, "slipToastRejected"),
        status === "Approved" ? "success" : "error"
      );
    } catch {
      showToast(t(lang, "slipActionFail"), "error");
    }
  }

  /* ✅ นับจำนวนแต่ละ tab */
  const countFor = (key) => {
    if (key === "All")     return payments.length;
    if (key === "NoSlip")  return payments.filter(p => p.status === "Pending" && !p.slip).length;
    return payments.filter(p => p.status === key).length;
  };

  /* ✅ กรองตาม tab + search */
  const filtered = payments.filter(p => {
    const matchTab =
      activeTab === "All"    ? true :
      activeTab === "NoSlip" ? (p.status === "Pending" && !p.slip) :
      p.status === activeTab;
    const q = search.toLowerCase();
    const matchSearch = !search ||
      (p.email || "").toLowerCase().includes(q) ||
      bookerName(p.bookingId || {}, p).toLowerCase().includes(q);
    return matchTab && matchSearch;
  });

  /* ── แบ่งหน้า: แสดง 50 รายการล่าสุด (payments เรียงใหม่→เก่ามาจาก backend แล้ว) ── */
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages - 1);      // กันค้างหน้าที่ไม่มีอยู่แล้ว
  const pageStart  = safePage * PAGE_SIZE;
  const visible    = showAll ? filtered : filtered.slice(pageStart, pageStart + PAGE_SIZE);
  const needsPager = filtered.length > PAGE_SIZE;

  if (forbidden) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center",
        minHeight: 300, fontSize: 14, color: "#991b1b" }}>
        {t(lang, "slipNoAccess")}
      </div>
    );
  }

  return (
    <div style={{ padding: "clamp(16px, 3vw, 24px)", maxWidth: 1560, margin: "0 auto" }}>
      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:none } }`}</style>

      {/* ── Top bar ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12, marginBottom: 20,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: "#1e293b", margin: 0 }}>
            {t(lang, "slipTitle")}
          </h1>
          <span style={{
            fontSize: 11, padding: "3px 10px", borderRadius: 20,
            background: "#dbeafe", color: "#1e40af", fontWeight: 600,
          }}>admin / staff</span>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t(lang, "slipSearch")}
            style={{
              fontSize: 13, padding: "6px 10px", height: 34, borderRadius: 8,
              border: "1px solid #e2e8f0", width: 180, color: "#1e293b",
              outline: "none",
            }}
          />
          <button onClick={loadPayments}
            style={{
              height: 34, padding: "0 14px", fontSize: 13, borderRadius: 8,
              border: "1px solid #e2e8f0", background: "#f8fafc",
              color: "#475569", cursor: "pointer",
            }}>
            {t(lang, "slipRefresh")}
          </button>
        </div>
      </div>

      {/* ✅ Filter Tabs */}
      {!loading && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
          {TABS.map(tab => {
            const count    = countFor(tab.key);
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 14px", borderRadius: 999,
                  border: `1px solid ${isActive ? "#111827" : "#e5e7eb"}`,
                  background: isActive ? "#111827" : "#fff",
                  color:      isActive ? "#fff"    : "#6b7280",
                  fontSize: 13, cursor: "pointer",
                  transition: "all .15s", fontFamily: "inherit", outline: "none",
                }}
              >
                {tab.key !== "All" && (
                  <span style={{
                    width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                    background: isActive ? "#fff" : tab.dot,
                  }} />
                )}
                {t(lang, tab.labelKey)}
                <span style={{
                  fontSize: 11, borderRadius: 999, padding: "1px 6px",
                  background: isActive ? "rgba(255,255,255,.2)" : "#f3f4f6",
                  color:      isActive ? "#fff" : "#6b7280",
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── จอแคบ: การ์ด ── */}
      {narrow ? (
        loading ? (
          <div style={{ textAlign: "center", padding: 32, color: "#94a3b8", fontSize: 13 }}>
            {t(lang, "slipLoading")}
          </div>
        ) : visible.length === 0 ? (
          <div style={{ textAlign: "center", padding: 32, color: "#94a3b8", fontSize: 13,
            border: "1px solid #e2e8f0", borderRadius: 12 }}>
            {t(lang, "slipNoItems")}
          </div>
        ) : (
          visible.map(p => (
            <TableRow key={p._id} payment={p} narrow isLast
              onOpen={() => setSelected(p)}
              onQuickAction={(status) => handleUpdate(p._id, status, "")}
              lang={lang} />
          ))
        )
      ) : (
      /* ── จอกว้าง: ตาราง ── */
      <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, overflowX: "auto" }}>
        {/* minWidth กันคอลัมน์บีบจนปุ่ม/วันที่ถูกตัด — จอแคบกว่านี้ให้เลื่อนแนวนอนแทน */}
        <table style={{
          width: "100%", minWidth: 1140, borderCollapse: "collapse",
          fontSize: 13, tableLayout: "fixed",
        }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              {/* แยกช่องเงินเป็น 2 ช่องเหมือนเดิม — ที่เดิมโดนบีบเพราะกล่องหน้าจำกัด 1280
                  ขยาย maxWidth เป็น 1560 แทน ทุกช่องเลยกว้างพอโดยไม่ต้องยุบ */}
              {[t(lang,"slipColBooker"),t(lang,"slipColRoom"),t(lang,"slipColPaid"),t(lang,"slipColTotal"),t(lang,"slipColPayType"),t(lang,"slipColStatus"),t(lang,"slipColDate"),t(lang,"slipColAction")]
                .map((h, i) => (
                  <th key={h} style={{
                    padding: "10px 12px", textAlign: "left", fontWeight: 600,
                    fontSize: 12, color: "#64748b", borderBottom: "1px solid #e2e8f0",
                    /* จัดการ 23% — แถว Pending มี 3 ปุ่ม / วันที่ 13% — รูปแบบตายตัว ห้ามโดนตัด
                       ช่องเงิน 8% พอสำหรับ "7,200 ฿" ที่ยาวสุดในระบบ */
                    width: ["14%","16%","8%","8%","10%","8%","13%","23%"][i],
                  }}>
                    {h}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: 32,
                  color: "#94a3b8", fontSize: 13 }}>
                  {t(lang, "slipLoading")}
                </td>
              </tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: 32,
                  color: "#94a3b8", fontSize: 13 }}>
                  {t(lang, "slipNoItems")}
                </td>
              </tr>
            ) : (
              visible.map((p, idx) => (
                <TableRow
                  key={p._id}
                  payment={p}
                  isLast={idx === visible.length - 1}
                  onOpen={() => setSelected(p)}
                  onQuickAction={(status) => handleUpdate(p._id, status, "")}
                  lang={lang}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* ── แถบแบ่งหน้า — โผล่เมื่อรายการเกิน PAGE_SIZE เท่านั้น ── */}
      {!loading && needsPager && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 10, marginTop: 14,
        }}>
          <span style={{ fontSize: 12.5, color: "#64748b" }}>
            {showAll
              ? t(lang, "slipShowingRange", 1, filtered.length, filtered.length)
              : t(lang, "slipShowingRange",
                  pageStart + 1, Math.min(pageStart + PAGE_SIZE, filtered.length), filtered.length)}
          </span>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {!showAll && (
              <>
                <PagerBtn label={t(lang, "slipPrevPage")} disabled={safePage === 0}
                  onClick={() => setPage(x => Math.max(0, x - 1))} />
                <span style={{ fontSize: 12.5, color: "#475569", fontWeight: 600 }}>
                  {t(lang, "slipPageOf", safePage + 1, totalPages)}
                </span>
                <PagerBtn label={t(lang, "slipNextPage")} disabled={safePage >= totalPages - 1}
                  onClick={() => setPage(x => Math.min(totalPages - 1, x + 1))} />
              </>
            )}
            <PagerBtn
              label={showAll ? t(lang, "slipShowPaged") : t(lang, "slipShowAll", filtered.length)}
              primary
              onClick={() => { setShowAll(v => !v); setPage(0); }} />
          </div>
        </div>
      )}

      {/* Modal */}
      {selected && (
        <PaymentModal
          payment={selected}
          onClose={() => setSelected(null)}
          showToast={showToast}
          lang={lang}
          onUpdate={async (id, status, note) => {
            await handleUpdate(id, status, note);
            setSelected(null);
          }}
        />
      )}

      <Toast {...toast} />
    </div>
  );
}

/* ─── Table Row ──────────────────────────────────────────────── */
function TableRow({ payment, isLast, onOpen, onQuickAction, lang, narrow = false }) {
  const [hovered, setHovered] = useState(false);
  /* การอนุมัติ/ปฏิเสธรอ backend ส่งอีเมลก่อนตอบกลับ ใช้เวลาหลายวินาที
     ถ้าไม่ล็อกปุ่มไว้ แอดมินจะกดซ้ำเพราะนึกว่าไม่ติด แล้วยิง request ซ้อนกัน */
  const [busy, setBusy] = useState(null);   // status ที่กำลังส่งอยู่ (Approved / Rejected)

  const runQuickAction = async (status) => {
    if (busy) return;
    setBusy(status);
    try {
      await onQuickAction(status);
    } finally {
      setBusy(null);
    }
  };

  const bk  = payment.bookingId || {};
  const sum = summarize(payment);
  const isGroup = sum.count > 1;

  /* ห้องเดียว: ชื่อประเภท / เลขห้อง
     หลายห้อง: "3 ห้อง · 2 ประเภท" / "เตียงคู่ ×2, เตียงใหญ่ ×1" */
  const roomMain = isGroup
    ? t(lang, "slipRoomsSummary", sum.count, sum.types.length)
    : (sum.types[0]?.name || "—");
  const roomSub = isGroup
    ? sum.types.map(x => `${x.name} ×${x.qty}`).join(", ")
    : (sum.rooms[0]?.roomNumber ? `${t(lang, "slipColRoom")} ${sum.rooms[0].roomNumber}` : "");

  const hasSlip = !!payment.slip;
  const isBookingCancelled = bk.status === "Cancelled" || bk.status === "PendingCancel";
  const isCash = bk.payType === "cash";
  // เงินสดไม่ต้องรอสลิป จึงไม่ใช่รายการ "รอแนบสลิป" — ให้กดอนุมัติได้ตามปกติ
  const isStaffNoSlip = payment.status === "Pending" && !hasSlip && !isBookingCancelled && !isCash;
  const booker  = bookerName(bk, payment);
  const payType = resolvePayType(bk);

  const tdBase = {
    padding: "10px 12px", color: "#1e293b",
    borderBottom: isLast ? "none" : "1px solid #f1f5f9",
    verticalAlign: "middle", overflow: "hidden",
    textOverflow: "ellipsis", whiteSpace: "nowrap",
    background: hovered ? "#f8fafc" : "transparent",
    transition: "background 0.1s",
  };

  /* เซลล์ 2 บรรทัดต้องปลด nowrap — tdBase ตั้ง nowrap+ellipsis ไว้ทุกช่อง
     ถ้าไม่ปลด บรรทัดล่างจะไปต่อท้ายบรรทัดบนแล้วโดนตัด */
  const tdWrap = { ...tdBase, whiteSpace: "normal", textOverflow: "clip" };
  const subLine = { fontSize: 11.5, color: "#94a3b8", marginTop: 2,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
  /* รายชื่อประเภทห้องยาวเกินช่องบ่อย — ให้ตัดบรรทัดแทนตัด "..." (จำกัด 2 บรรทัดกันแถวสูงเกิน)
     ต่างจากอีเมลที่เป็นคำเดียว ตัดบรรทัดแล้วดูแย่กว่า เลยยังใช้ ellipsis */
  const subLineWrap = { ...subLine, whiteSpace: "normal", display: "-webkit-box",
                        WebkitLineClamp: 2, WebkitBoxOrient: "vertical" };

  /* ปุ่มชุดเดียวกันใช้ทั้งตารางและการ์ด — แยกออกมาไม่ให้เขียนซ้ำสองที่ */
  const actions = isBookingCancelled ? (
    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      <span style={{
        fontSize: 11, padding: "2px 10px", borderRadius: 20,
        background: "#fee2e2", color: "#991b1b",
        border: "1px solid #fca5a5", fontWeight: 600,
      }}>
        {bk.status === "PendingCancel" ? t(lang, "slipPendingCancelTag") : t(lang, "slipCancelledTag")}
      </span>
      <ActionBtn label={t(lang, "slipViewDetail")}
        bg="#f1f5f9" color="#475569" border="#e2e8f0" onClick={onOpen} />
    </div>
  ) : isStaffNoSlip ? (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      <ActionBtn label={t(lang, "slipAddSlip")}
        bg="#e0f2fe" color="#0369a1" border="#7dd3fc" disabled={!!busy} onClick={onOpen} />
      <ActionBtn label={t(lang, busy === "Rejected" ? "slipProcessing" : "slipReject")}
        bg="#fee2e2" color="#991b1b" border="#fca5a5" disabled={!!busy}
        onClick={() => runQuickAction("Rejected")} />
    </div>
  ) : payment.status === "Pending" ? (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      <ActionBtn label={t(lang, isCash ? "slipViewDetail" : "slipViewSlip")}
        bg="#dbeafe" color="#1e40af" border="#93c5fd" disabled={!!busy} onClick={onOpen} />
      <ActionBtn label={t(lang, busy === "Approved" ? "slipProcessing" : "slipApprove")}
        bg="#d1fae5" color="#065f46" border="#6ee7b7" disabled={!!busy}
        onClick={() => runQuickAction("Approved")} />
      <ActionBtn label={t(lang, busy === "Rejected" ? "slipProcessing" : "slipReject")}
        bg="#fee2e2" color="#991b1b" border="#fca5a5" disabled={!!busy}
        onClick={() => runQuickAction("Rejected")} />
    </div>
  ) : (
    <ActionBtn label={t(lang, "slipViewDetail")}
      bg="#f1f5f9" color="#475569" border="#e2e8f0" onClick={onOpen} />
  );

  /* ── จอแคบ: การ์ด ── */
  if (narrow) {
    const label = { fontSize: 11, color: "#94a3b8", flexShrink: 0 };
    const line  = { display: "flex", justifyContent: "space-between",
                    alignItems: "flex-start", gap: 10, fontSize: 13, marginTop: 6 };
    return (
      <div style={{
        border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff",
        padding: 14, marginBottom: 10,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between",
          alignItems: "flex-start", gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 14,
              overflow: "hidden", textOverflow: "ellipsis" }}>{booker}</div>
            {payment.email && payment.email !== booker && (
              <div style={{ ...subLine, marginTop: 1 }}>{payment.email}</div>
            )}
          </div>
          <StatusPill status={payment.status} lang={lang} />
        </div>

        <div style={{ borderTop: "1px solid #f1f5f9", marginTop: 10, paddingTop: 8 }}>
          <div style={line}>
            <span style={label}>{t(lang, "slipColRoom")}</span>
            <span style={{ textAlign: "right", minWidth: 0 }}>
              <div style={{ fontWeight: isGroup ? 600 : 400, color: "#1e293b" }}>{roomMain}</div>
              {roomSub && <div style={{ ...subLine, whiteSpace: "normal" }}>{roomSub}</div>}
            </span>
          </div>
          <div style={line}>
            <span style={label}>{t(lang, "slipColTotal")}</span>
            <span style={{ fontWeight: 600, color: "#1e293b" }}>
              {fmtMoney(sum.total)}
              <span style={{ ...label, marginLeft: 8 }}>
                ({t(lang, "slipColPaid")} {fmtMoney(sum.paid)})
              </span>
            </span>
          </div>
          <div style={{ ...line, alignItems: "center" }}>
            <span style={label}>{t(lang, "slipColPayType")}</span>
            <PayTypePill type={payType} lang={lang} />
          </div>
          <div style={line}>
            <span style={label}>{t(lang, "slipColDate")}</span>
            <span style={{ color: "#64748b" }}>{fmtDate(payment.createdAt)}</span>
          </div>
        </div>

        <div style={{ borderTop: "1px solid #f1f5f9", marginTop: 10, paddingTop: 10 }}>
          {actions}
        </div>
      </div>
    );
  }

  /* ── จอกว้าง: แถวตาราง ── */
  return (
    <tr onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <td style={tdWrap} title={booker}>
        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{booker}</div>
        {payment.email && payment.email !== booker && (
          <div style={subLine} title={payment.email}>{payment.email}</div>
        )}
      </td>
      <td style={tdWrap} title={roomSub || roomMain}>
        <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      fontWeight: isGroup ? 600 : 400 }}>{roomMain}</div>
        {roomSub && <div style={subLineWrap}>{roomSub}</div>}
      </td>
      {/* ยอดต้องเป็นของทั้งกลุ่ม ไม่ใช่ของ booking ใบแรกใบเดียว (เดิมจองหลายห้องแล้วยอดผิด) */}
      <td style={tdBase}>{fmtMoney(sum.paid)}</td>
      <td style={{ ...tdBase, fontWeight: 600 }}>{fmtMoney(sum.total)}</td>
      <td style={tdBase}><PayTypePill type={payType} lang={lang} /></td>
      <td style={tdBase}><StatusPill status={payment.status} lang={lang} /></td>
      <td style={tdBase} title={fmtDate(payment.createdAt)}>{fmtDate(payment.createdAt)}</td>
      {/* ช่องปุ่มต้องไม่ ellipsis ไม่งั้นปุ่มสุดท้ายโดนตัดครึ่ง */}
      <td style={{ ...tdBase, overflow: "visible", textOverflow: "clip", padding: "10px 12px 10px 8px" }}>
        {actions}
      </td>
    </tr>
  );
}

function PagerBtn({ label, onClick, disabled = false, primary = false }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        fontSize: 12.5, padding: "7px 14px", borderRadius: 8, fontWeight: 600,
        fontFamily: "inherit", whiteSpace: "nowrap",
        cursor: disabled ? "not-allowed" : "pointer",
        background: disabled ? "#f8fafc" : primary ? "#1e293b" : "#fff",
        color:      disabled ? "#cbd5e1" : primary ? "#fff"    : "#475569",
        border: `1px solid ${disabled ? "#e2e8f0" : primary ? "#1e293b" : "#cbd5e1"}`,
      }}>
      {label}
    </button>
  );
}

function ActionBtn({ label, bg, color, border, onClick, disabled = false }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        fontSize: 11, padding: "4px 10px",
        cursor: disabled ? "not-allowed" : "pointer",
        background: bg, color, border: `1px solid ${border}`,
        borderRadius: 6, fontWeight: 600, whiteSpace: "nowrap",
        fontFamily: "inherit", flexShrink: 0,
        opacity: disabled ? 0.5 : 1, transition: "opacity .15s",
      }}>
      {label}
    </button>
  );
}